import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { preparseClientFile, sha256Hex, needsServerParse } from "@/lib/instructorImport/clientParse";
import { AlertTriangle } from "lucide-react";

type Props = {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onParsed: (importId: string) => void;
};

export default function ImportUploadDialog({ open, onOpenChange, onParsed }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [sourceName, setSourceName] = useState("Holly's Instructor Records");
  const [sourcePerson, setSourcePerson] = useState("");
  const [sourceDate, setSourceDate] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [duplicate, setDuplicate] = useState<any>(null);
  const [pendingArgs, setPendingArgs] = useState<any>(null);

  const reset = () => {
    setFile(null); setSourcePerson(""); setSourceDate(""); setNote("");
    setDuplicate(null); setPendingArgs(null); setBusy(false);
  };

  const submit = async (ignoreDuplicate = false) => {
    if (!file) return toast.error("Choose a file");
    if (!sourceName.trim()) return toast.error("Enter a source name");
    if (file.size > 20 * 1024 * 1024) return toast.error("File must be under 20 MB");

    setBusy(true);
    try {
      const checksum = await sha256Hex(file);
      const parsed = needsServerParse(file)
        ? { kind: "needs_ai" as const, reason: `Server parse for ${file.type || file.name}` }
        : await preparseClientFile(file);

      // Upload file to private bucket
      const path = `${crypto.randomUUID()}/${file.name}`;
      const { error: upErr } = await supabase.storage.from("instructor-imports").upload(path, file, { upsert: false });
      if (upErr) throw upErr;

      const args = {
        filename: file.name,
        storage_path: path,
        file_type: file.type || "application/octet-stream",
        file_size: file.size,
        checksum,
        source_name: sourceName.trim(),
        source_person: sourcePerson.trim() || null,
        source_date: sourceDate || null,
        internal_note: note.trim() || null,
        pre_parsed: parsed,
        ignore_duplicate: ignoreDuplicate,
      };

      const { data, error } = await supabase.functions.invoke("parse-instructor-import", { body: args });
      if (error) throw error;

      if (data?.duplicate_of) {
        setDuplicate(data.duplicate);
        setPendingArgs(args);
        setBusy(false);
        return;
      }

      if (data?.import_id) {
        toast.success(`Parsed ${data.records_detected} record(s). Ready for review.`);
        onParsed(data.import_id);
        reset();
      } else {
        toast.error(data?.error ?? "Parse failed");
      }
    } catch (e: any) {
      toast.error(e?.message ?? "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Import Instructor File</DialogTitle>
          <DialogDescription>
            Upload a spreadsheet, PDF, Word doc, text, or image. The system will extract instructor
            information, match it to existing profiles, and let you review every change before it
            is saved. Files are stored privately and only visible to admins.
          </DialogDescription>
        </DialogHeader>

        {duplicate ? (
          <div className="space-y-3">
            <div className="rounded-md border border-amber-500/40 bg-amber-50 p-3 text-sm dark:bg-amber-950/30">
              <div className="flex items-center gap-2 font-medium">
                <AlertTriangle className="h-4 w-4" />
                This file may already have been imported.
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                Previous import: <span className="font-mono">{duplicate.filename}</span> — {new Date(duplicate.created_at).toLocaleString()} — status: {duplicate.status}
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => onParsed(duplicate.id)}>View Previous Import</Button>
              <Button variant="outline" onClick={() => setDuplicate(null)}>Cancel</Button>
              <Button onClick={() => submit(true)} disabled={busy}>Continue Anyway</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label>File</Label>
              <Input
                type="file"
                accept=".csv,.xlsx,.xls,.pdf,.docx,.txt,.png,.jpg,.jpeg,.webp"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
              <div className="text-xs text-muted-foreground mt-1">
                Do not upload W-9s or documents containing full SSN/EIN numbers here — those belong in the onboarding workflow.
              </div>
            </div>
            <div>
              <Label>Source name</Label>
              <Input value={sourceName} onChange={(e) => setSourceName(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Source person (optional)</Label>
                <Input value={sourcePerson} onChange={(e) => setSourcePerson(e.target.value)} placeholder="e.g. Holly Smith" />
              </div>
              <div>
                <Label>Source date (optional)</Label>
                <Input type="date" value={sourceDate} onChange={(e) => setSourceDate(e.target.value)} />
              </div>
            </div>
            <div>
              <Label>Internal note (optional)</Label>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
              <Button onClick={() => submit(false)} disabled={busy || !file}>
                {busy ? "Uploading & parsing…" : "Upload & Parse"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
